import {Component, Inject, OnInit, ViewEncapsulation} from '@angular/core';

import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Proposal} from '../../../model/proposal/proposal';
import pdfMake from 'pdfmake/build/pdfmake';
import {Formats} from '../../../constants/formats';
import {DatePipe, DecimalPipe} from '@angular/common';
import {LocaleService} from '../../../services/locale/locale.service';
import {TranslateService} from '@ngx-translate/core';
import {ProposalTile} from '../../../model/proposal-tile';
import {UserService} from '../../../services/user/user.service';
import LineOfBusinessColor from '../../../../assets/config/line-of-business-color.json';
import {ProposalService} from '../../../services/proposal-service/proposal.service';
import {Store} from '@ngrx/store';
import {ProposalAppState} from '../../../store/proposal/reducers/proposal.reducer';
import {ProposalDetailPageActions} from '../../../store/proposal/actions/proposal.actions';
import {FileUploadService} from '../../../services/file-upload-service/file-upload.service';

@Component({
    selector: 'app-share-proposal-pdf-dialog',
    templateUrl: './share-proposal-pdf-dialog.html',
    styleUrls: ['./share-proposal-pdf-dialog.scss', '../../../../styles/create-something.scss', '../../../../styles/form-input.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class SharePDFDialogComponent implements OnInit{

  email: string = '';
  proposal: Proposal;
  proposalTile: ProposalTile;
  backgroundImageIcon: string;
  userImage = '';
  topRightLogoImage = '';
  showSizeWarning: boolean = false;
  gdprConsentChecked: boolean = false;

  profileImageStandard =  '';

  constructor(public dialogRef: MatDialogRef<SharePDFDialogComponent>,

              private readonly datePipe: DatePipe,
              readonly proposalService: ProposalService,
              private readonly proposalStore: Store<ProposalAppState>,
              readonly userService: UserService,
              private readonly number: DecimalPipe,
              private readonly localeService: LocaleService,
              private readonly translate: TranslateService,
              protected fileUploadService: FileUploadService,
              @Inject(MAT_DIALOG_DATA) data) {

    this.proposal = data.proposal;
    this.proposalTile = data.proposalTile;
  }

  ngOnInit(): void {
    this.loadDefaultProfileImage();
    this.userService.getProfilePicture(this.proposalTile.userId).subscribe(img => {
      this.userImage = img ?? this.profileImageStandard;
    });
    this.loadTopRightLogo();
    const totalBytes = (this.proposal.proposalInformation?.attachedFiles ?? [])
      .reduce((sum, f) => sum + (f.sizeInBytes ?? 0), 0);
    this.showSizeWarning = totalBytes > 10 * 1024 * 1024;
  }

  async shareViaEmail() {
    const pdfBlob = new Blob([await this.downloadPDF()], { type: 'application/pdf' });

    this.proposalService
      .downloadEMLFile(this.proposalTile.id, this.email, pdfBlob)
      .subscribe((emlBlob: Blob) => {

        const url = window.URL.createObjectURL(emlBlob);

        const a = document.createElement('a');
        a.href = url;
        a.download = "email.eml";
        a.click();

        window.URL.revokeObjectURL(url);
      });
    this.saveProposalInfoEmail();
  }

  saveProposalInfoEmail() {
    const today = new Date();
    const formattedDate =
      String(today.getDate()).padStart(2, '0') + '.' +
      String(today.getMonth() + 1).padStart(2, '0') + '.' +
      today.getFullYear();
    this.proposalService.addEmailToProposal(this.proposal.proposalInformation.id, this.email + "/" + formattedDate)?.subscribe(() => {
      this.proposalStore.dispatch(ProposalDetailPageActions.load({proposalId: this.proposal.proposalInformation.id}));
    });
  }

  isValidEmail(email: string): boolean {
 const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; return emailRegex.test(email);
}

  private async loadLineOfBusinessIcon(proposal: Proposal): Promise<string> {
    const iconName = this.getLineOfBusinessIconName(proposal);
    const imageUrl = `assets/img/line-of-business/${iconName}.svg`;

    try {
      return await this.svgToPng(imageUrl, 160,80, 4,LineOfBusinessColor[iconName]+'26',12);
    } catch (error) {
      console.error('Failed to load icon:', error);
      return ''; // Return empty string if conversion fails
    }
  }

  private async svgToPng(
    svgUrl: string,
    outerSize = 120,
    iconSize = 80,
    scale = 4,              // higher = better quality
    background = '#ffffff', // or 'transparent'
    radius = 16             // corner radius
  ): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        const canvas = document.createElement('canvas');

        // High-res internal canvas
        const scaledSize = outerSize * scale;
        canvas.width = scaledSize;
        canvas.height = scaledSize;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }

        // Scale drawing operations
        ctx.scale(scale, scale);

        // Rounded clip
        this.roundedRect(ctx, 0, 0, outerSize, outerSize, radius);
        ctx.clip();

        // Background
        if (background !== 'transparent') {
          ctx.fillStyle = background;
          ctx.fillRect(0, 0, outerSize, outerSize);
        }

        const offset = (outerSize - iconSize) / 2;

        // Draw SVG
        ctx.drawImage(img, offset, offset, iconSize, iconSize);

        resolve(canvas.toDataURL('image/png'));
      };

      img.onerror = () => reject(new Error('Failed to load SVG'));
      img.src = svgUrl;
    });
  }


  private roundedRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ) {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }


  async downloadPDF() : Promise<Blob> {

    const language = localStorage.getItem('lang') || navigator.language.slice(0,2);
    const isEnglish = language === 'en';

    const lineOfBusinessIconData = await this.loadLineOfBusinessIcon(this.proposal);

    await this.loadBackgroundImage();

    const docDefinition: any = {
      background: [
        {
          image: this.backgroundImageIcon,
          width: 595,
          height: 842,
        }
      ],
      filename: isEnglish ? "Proposal - " + this.proposal.proposalInformation.proposalName + ".pdf" : "Ausschreibung - " + this.proposal.proposalInformation.proposalName + ".pdf",
      pageSize: 'A4',
      styles: {
        title: {
          fontSize: 20,
          alignment: 'left',
          margin: [0, 0, 0, 15],
          color: '#14142B',
        },
        descriptor: {
          color: '#14142B',
          fontSize: 8,
          margin: [0, 10, 0, 1],
        },
        value: {
          color: '#14142B',
          fontSize: 8,
          bold: false,
          margin: [0, 10, 0, 1],
        },
        tableTitle: {
          fontSize: 9,
          color: '#14142B',
          bold: true,
          alignment: 'left',
          margin: [0, 16, 0, 4]
        },
        tableHeader: {
          fillColor: '#EFF0F7',
          fontSize: 10,
        }
      },
      content: this.getProposalContent(this.proposal, this.proposalTile, lineOfBusinessIconData)
    };

    return new Promise((resolve) => {
      pdfMake.createPdf(docDefinition).getBlob((blob: Blob) => {
        resolve(blob);
      });
    });

  }

  private getProposalContent(proposal: Proposal, proposalTile: ProposalTile,lineOfBusinessIconData: string): any[] {
    const content: any[] = [
      this.getProposalTileSection(proposal, proposalTile,lineOfBusinessIconData)
      ];
      // Partner Information Section
    if (proposal.partner?.name) {
      content.push(
      {
        style: 'tableTitle',
          text
      :
        this.translate.instant('proposalDetails.partnerInformation'),
          margin
      :
        [0, 0, 0, 5]
      }
    ,
      {
        columns: [
          {width: '40%', style: 'descriptor', text: this.translate.instant('proposalDetails.partnerName')},
          {width: '60%', style: 'value', text: proposal.partner?.name || '-'}
        ],
          margin
      :
        [0, 0, 0, 0]
      }
    ,
      {
        columns: [
          {width: '40%', style: 'descriptor', text: this.translate.instant('proposalDetails.streetNumber')},
          {width: '60%', style: 'value', text: `${proposal.partner?.street || ''} ${proposal.partner?.houseNumber || ''}`.trim() || '-'}
        ],
          margin
      :
        [0, 0, 0, 0]
      }
    ,
      {
        columns: [
          {width: '40%', style: 'descriptor', text: this.translate.instant('proposalDetails.zipLocation')},
          {width: '60%', style: 'value', text: `${proposal.partner?.postalCode || ''} ${proposal.partner?.location || ''}`.trim() || '-'}
        ],
          margin
      :
        [0, 0, 0, 0]
      }
    ,
      {
        columns: [
          {
            width: '40%',
            style: 'descriptor',
            text: (this.translate.instant('proposalDetails.lkz') + ", " + this.translate.instant('proposalDetails.country'))
          },
          {width: '60%', style: 'value', text: `${proposal.partner?.countryCode || ''} ${proposal.partner?.countryName || ''}`.trim() || '-'}
        ],
          margin
      :
        [0, 0, 0, 20]
      }
    ,

      this.horizontalLine(),
      )
    }
    content.push(
      // The Risk Section
      {
        style: 'tableTitle',
        text: this.translate.instant('proposalDetails.theRisk'),
        margin: [0, 0, 0, 5]
      },
      {
        columns: [
          { width: '40%', style: 'descriptor', text: this.translate.instant('proposalDetails.requestedPercentage') },
          { width: '60%', style: 'value', text: proposal.proposalInformation.coveragePercentageRequested }
        ],
        margin: [0, 0, 0, 0]
      },
      {
        columns: [
          { width: '40%', style: 'descriptor', text: this.translate.instant('proposalDetails.requestedRoles') },
          { width: '60%', style: 'value', text: `${proposal.proposalInformation.leadInsurerNeeded ? (this.translate.instant('proposalDetails.leader') + ", " ) : ''} ${proposal.proposalInformation.followingInsurerNeeded ? (this.translate.instant('proposalDetails.follower') ) : ''}` }
        ],
        margin: [0, 0, 0, 0]
      },
      {
        columns: [
          { width: '40%', style: 'descriptor', text: this.translate.instant('createProposal.beginDate') },
          { width: '60%', style: 'value', text: this.datePipe.transform(proposal.proposalInformation.riskDateBegin, 'dd.MM.yyyy') }
        ],
        margin: [0, 0, 0, 0]
      },
      {
        columns: [
          { width: '40%', style: 'descriptor', text: this.translate.instant('createProposal.endDate') },
          { width: '60%', style: 'value', text: this.datePipe.transform(proposal.proposalInformation.riskDateEnd, 'dd.MM.yyyy') }
        ],
        margin: [0, 0, 0, 0]
      },
      {
        columns: [
          { width: '40%', style: 'descriptor', text: this.translate.instant('createProposal.mainDueDate') },
          { width: '60%', style: 'value', text: proposal.proposalInformation.mainDueDate }
        ],
        margin: [0, 0, 0, 0]
      },
      {
        columns: [
          { width: '40%', style: 'descriptor', text: this.translate.instant('createProposal.lineOfBusiness') },
          { width: '60%', style: 'value', text: this.translate.instant(`lineOfBusiness.clauses.${proposal.lineOfBusiness?.number}.name`) }
        ],
        margin: [0, 0, 0, 20]
      },

      this.horizontalLine(),
      // Sums Section
      {
        style: 'tableTitle',
        text: this.translate.instant('proposalDetails.sums'),
        margin: [0, 0, 0, 5]
      },
      {
        columns: [
          { width: '40%', style: 'descriptor', text: this.translate.instant('createProposal.insuredAmount') },
          { width: '60%', style: 'value', text: this.number.transform(proposal.proposalInformation.insuredAmount, Formats.CURRENCY, this.localeService.getLocale()) + (proposal.lineOfBusiness.number === '140' && proposal.proposalInformation.insuredSumType === 'VALUES_1914' ? '' : ' ' + proposal.proposalInformation.currency) }
        ],
        margin: [0, 0, 0, 0]
      },
    );

    if (proposal.proposalInformation.amountDescription) {
      content.push({
        columns: [
          { width: '40%', style: 'descriptor', text: this.translate.instant('proposalSlip.typeOfInsuredSum') },
          { width: '60%', style: 'value', text: proposal.proposalInformation.amountDescription }
        ],
        margin: [0, 0, 0, 0]
      });
    }

    if (proposal.lineOfBusiness.number === '140' && proposal.proposalInformation.insuredSumType) {
      content.push({
        columns: [
          { width: '40%', style: 'descriptor', text: this.translate.instant('createProposal.housingInsuranceSumType') },
          { width: '60%', style: 'value', text: this.translate.instant(`createProposal.housingInsuranceSumTypeOptions.${proposal.proposalInformation.insuredSumType}`) }
        ],
        margin: [0, 0, 0, 0]
      });
    }

    content.push({ text: '', margin: [0, 0, 0, 20] });



    // Risk Description Section (if exists)
    if (proposal.proposalInformation.riskDescriptionFreeText) {
      content.push(
        this.horizontalLine(),
        {
          style: 'tableTitle',
          text: this.translate.instant('proposalDetails.riskDescription'),
          margin: [0, 0, 0, 5]
        },
        {
          style: 'value',
          text: proposal.proposalInformation.riskDescriptionFreeText,
          margin: [0, 0, 0, 20]
        }
      );
    }

    // Risk Locations Section (if exists)
    if (proposal.riskLocations?.length > 0) {
      content.push(
        this.horizontalLine(),
        {
          style: 'tableTitle',
          text: this.translate.instant('proposalDetails.riskLocation'),
          margin: [0, 0, 0, 5]
        }
      );
      proposal.riskLocations.forEach((location, index) => {
        content.push(
          {columns: [
          { width: '40%', style: 'descriptor', text: this.translate.instant('proposalDetails.streetNumber'), margin: [0, 0, 0, 0] },
          {
            width: '60%',
            style: 'value',
            text: `${location.street || ''} ${location.houseNumber || ''}`.trim(),
            margin: [0, 0, 0, 0]
          }
        ]},
          {columns: [
              { width: '40%', style: 'descriptor', text: this.translate.instant('proposalDetails.zipLocation'), margin: [0, 0, 0, 0] },
              {
                width: '60%',
                style: 'value',
                text: `${location.postalCode || ''} ${location.location || ''}`.trim(),
                margin: [0, 0, 0, 0]
              }
            ]},
          {columns: [
              { width: '40%', style: 'descriptor', text: (this.translate.instant('createProposal.countryCode') + ", "+this.translate.instant('createProposal.country')), margin: [0, 0, 0, 0] },
              {
                width: '60%',
                style: 'value',
                text: `${location.countryCode || ''} ${location.countryName || ''}`.trim(),
                margin: [0, 0, 0, index === proposal.riskLocations.length - 1 ? 5 : 20]
              }
            ]},
        );
      });
    }

    // Previous Damages Section (if exists)
    if (proposal.previousDamages?.length > 0) {
      content.push(
        this.horizontalLine(),
        {
          style: 'tableTitle',
          text: this.translate.instant('proposalDetails.previousDamages'),
          margin: [0, 0, 0, 5]
        },
        {
        columns: [
        { width: '20%', style: 'descriptor', text: this.translate.instant('createProposal.previousDamageExists') + "?"},
        { width: '80%', style: 'value', text: this.translate.instant('proposalDetails.yes') }
      ]}
      );
      proposal.previousDamages.forEach((damage, index) => {
        content.push(
          { width: '20%', style: 'descriptor', text: damage.year?.toString() || '-' },
          {
          columns: [
            { width: '20%', style: 'descriptor', text: this.translate.instant('createProposal.amountPaid') },
            { width: '80%', style: 'value', text: this.number.transform(damage.amountPaid, Formats.CURRENCY, this.localeService.getLocale()) + damage.currency}
          ],
          margin: [0, 0, 0, 0]
        },
          {
            columns: [
              { width: '20%', style: 'descriptor', text: this.translate.instant('createProposal.damageType') },
              { width: '80%', style: 'value', text: damage.type || '-'}
            ],
            margin: [0, 0, 0, index === proposal.previousDamages.length - 1 ? 20 : 5]
          }
          );
      });
    }

    if (proposal.proposalInformation.individualClausesFreeText){
      content.push(
        this.horizontalLine(),
        {
          style: 'tableTitle',
          text: this.translate.instant('createProposal.individualClausesPlaceholder'),
          margin: [0, 0, 0, 5]
        },
        {
                style: 'value',
                text: proposal.proposalInformation.individualClausesFreeText,
                margin: [0, 0, 0, 20]
        }
        );
    }


    if (proposal.proposalInformation.individualConditionsFreeText){
      content.push(
        this.horizontalLine(),
        {
          style: 'tableTitle',
          text: this.translate.instant('createProposal.individualConditionsPlaceholder'),
          margin: [0, 0, 0, 5]
        },
            {
              style: 'value',
              text: proposal.proposalInformation.individualConditionsFreeText,
              margin: [0, 0, 0, 20]
            }
            );
    }

    return content;
  }

  getLineOfBusinessIconName(proposal: Proposal): string {
    let imageName = '';
    const number = proposal.lineOfBusiness.number;

    if (number.startsWith('04')) {
      imageName = 'Haftpflicht';
    } else if (number.startsWith('06')) {
      imageName = 'Luftfahrt';
    } else if (number.startsWith('08')) {
      imageName = 'industrie';
    } else if (number.startsWith('17')) {
      imageName = 'Technische';
    } else if (number.startsWith('18')) {
      imageName = 'Einheitsversicherung';
    } else if (number.startsWith('19')) {
      imageName = 'Transport';
    } else if (number === '231') {
      imageName = 'Feuer';
    } else if (number === '140') {
      imageName = 'Wohngebaeude';
    } else if (number === '232') {
      imageName = 'Technische_Betriebsunterbrechung';
    } else if (number === '233' || number.startsWith('29')) {
      imageName = 'Sonstige_Versicherung';
    } else if (number === '630') {
      imageName = 'Rahmen_und_Sammelvertraege_Multi-Line';
    } else {
      imageName = 'missing';
    }


    return imageName;
  }

  private horizontalLine(
    color = '#C4C4C4',
    thickness = 1,
    margin: [number, number, number, number] = [0, 8, 0, 8]
  ): any {
    return {
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: 515,
          y2: 0,
          lineWidth: thickness,
          lineColor: color
        }
      ],
      margin
    };
  }

  private getProposalTileSection(proposal: Proposal, proposalTile: ProposalTile, lineOfBusinessIconData: string): any {

    return {
      columns: [
        {
          width: 140,
          stack: [
            lineOfBusinessIconData ? {
              image: lineOfBusinessIconData,
              width: 140,
              height: 140,
              alignment: 'center',
              margin: [0, 5, 0, 5]
            } : {},
          ],
          fillColor: LineOfBusinessColor[this.getLineOfBusinessIconName(proposal)] + '26' || '#EFF0F7',
        },
        {
          width: '*',
          margin: [10, 10, 0, 0],
          stack: [
            {
              fontSize: 14,
              bold: true,
              columns: [
                {
                  margin: [0, 0, 0, 5],
                  stack: [
                    {text: proposal.proposalInformation.proposalName || `#${proposal.proposalInformation.id.slice(0, 8)}`, alignment: 'down'},
                  ]
                },
                {
                  image: this.topRightLogoImage,
                  fit: [100, 30],
                  margin: [0, 5, 0, 5],
                  alignment: 'right'
                }
                ]
            },
            {
              margin: [0, 10, 0, 0],
                columns: [
                  {
                    width: '25%',
                    stack: [
                      {text: this.translate.instant('createProposal.sumInsured'), fontSize: 7, color: '#6E7191'},
                      {text: this.number.transform(proposal.proposalInformation.insuredAmount, Formats.CURRENCY), fontSize: 11, bold: true}
                    ]
                  },
                  {
                    width: '10%',
                    stack: [
                      {text: this.translate.instant('sorting.coverage'), fontSize: 7, color: '#6E7191'},
                      {text: `${(proposalTile.coverage * 100).toFixed(2)}%`, fontSize: 11, bold: true, color: this.coverageColor()}
                    ]
                  },
                  {
                    width: '35%',
                    stack: [
                      {text: this.translate.instant('offer.lineOfBusiness'), fontSize: 7, color: '#6E7191'},
                      {
                        text: this.translate.instant(`lineOfBusiness.clauses.${proposal.lineOfBusiness?.number}.name`),
                        fontSize: 11,
                        bold: true,
                      }
                    ]
                  },
                  {
                    width: '25%',
                    alignment: 'right',
                    stack: [
                      {text: this.translate.instant('deadline2'), fontSize: 7, color: '#6E7191'},
                      {text: this.datePipe.transform(proposal.proposalInformation.deadline, 'dd.MM.yyyy'), fontSize: 11, bold: true}
                    ]
                  }
                ],
            },
            {
              columns: [
                {
                  width: 40,
                  image: this.userImage || this.profileImageStandard,
                  height: 40,
                  margin: [0, 5, 0, 5],
                  alignment: 'center'
                },
                {
                  width: '*',
                  stack: [
                    { text: proposalTile.firstName + " " + proposalTile.lastName, fontSize: 10, bold: true },
                    { text: proposalTile.companyName, fontSize: 8, color: '#6E7191' },
                  ],
                  margin: [5, 0, 0, 0]
                },
                this.horizontalLine()
              ],
              margin: [0, 10, 0, 0]
            }
          ]
        },
      ],
      margin: [0, 0, 0, 15]
    };
  }

  coverageColor() {
    if (this.proposalTile.coverage < 1 / 3) {
      return '#AB1400';
    } else if (this.proposalTile.coverage < 2 / 3) {
      return '#946300';
    } else {
      return '#4D9407';
    }
  }

  private loadBackgroundImage(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const imageUrl = '../../../assets/slip/watermark-siegel.png';
      const xhr = new XMLHttpRequest();
      xhr.open('GET', imageUrl, true);
      xhr.responseType = 'blob';

      xhr.onload = () => {
        const reader = new FileReader();
        reader.onload = () => {
          this.backgroundImageIcon = reader.result as string;
          resolve();
        };

        reader.readAsDataURL(xhr.response);
      };

      xhr.onerror = () => reject(new Error('Failed to load image'));
      xhr.send();
    });
  }

  private loadDefaultProfileImage(): void {

      const imageUrl = 'assets/img/profile/default-profile-icon.png';
      const xhr = new XMLHttpRequest();
      xhr.open('GET', imageUrl, true);
      xhr.responseType = 'blob';

      xhr.onload = () => {
        const reader = new FileReader();
        reader.onload = () => {
          this.profileImageStandard = reader.result as string;
        };
        reader.readAsDataURL(xhr.response);
      };

      xhr.onerror = () => {
        console.error('Failed to load default profile image');
        this.profileImageStandard = '';
      };
      xhr.send();
  }

  private loadTopRightLogo(): void {

    const imageUrl = 'assets/slip/HVB_logo.png';
    const xhr = new XMLHttpRequest();
    xhr.open('GET', imageUrl, true);
    xhr.responseType = 'blob';

    xhr.onload = () => {
      const reader = new FileReader();
      reader.onload = () => {
        this.topRightLogoImage = reader.result as string;
      };
      reader.readAsDataURL(xhr.response);
    };

    xhr.onerror = () => {
      console.error('Failed to logo image');
      this.topRightLogoImage = '';
    };
    xhr.send();
  }
}
